import React, { useState, useMemo } from 'react'
import type { TeamLite, PlayerRow, AvailabilityStatus, InjuryInfo } from '../../../types'
import { useFixturePlayers } from '../../../swr-config'
import { countryToEmoji, classifyPosition } from '../utils/countryFlags'
import type { PositionGroup } from '../types'

interface MALineupTabProps {
  fixtureId: number
  homeTeam: TeamLite
  awayTeam: TeamLite
}

interface GroupedPlayers {
  GK: PlayerRow[]
  DEF: PlayerRow[]
  MID: PlayerRow[]
  FWD: PlayerRow[]
}

function groupPlayersByPosition(players: PlayerRow[]): GroupedPlayers {
  const groups: GroupedPlayers = { GK: [], DEF: [], MID: [], FWD: [] }

  players.forEach((player) => {
    const group = classifyPosition(player.position)
    groups[group].push(player)
  })

  // Sort each group by start probability descending
  Object.keys(groups).forEach((key) => {
    groups[key as PositionGroup].sort((a, b) => b.start_probability - a.start_probability)
  })

  return groups
}

function AvailabilityBadge({ status, reason }: { status: AvailabilityStatus | null | undefined; reason?: string | null }) {
  if (!status || status === 'available' || status === 'confirmed_in') return null

  const badges: Record<string, { label: string; className: string }> = {
    injured: { label: 'INJ', className: 'injured' },
    doubtful: { label: 'DBT', className: 'doubtful' },
    suspended: { label: 'SUS', className: 'suspended' },
    confirmed_out: { label: 'OUT', className: 'injured' },
  }

  const badge = badges[status]
  if (!badge) return null

  return (
    <span
      className={`availability-badge ${badge.className}`}
      title={reason || status}
    >
      {badge.label}
    </span>
  )
}

interface PlayerRowDisplayProps {
  player: PlayerRow
  compact: boolean
  showStats: boolean
  isConfirmed?: boolean
}

function PlayerRowDisplay({ player, compact, showStats, isConfirmed }: PlayerRowDisplayProps) {
  const seasonStats = player.windows?.season || {}
  const nationality = (player as any).nationality
  const age = (player as any).age
  const flag = countryToEmoji(nationality)

  // Check if badge will be shown to reserve space
  const hasBadge = !isConfirmed && player.availability_status &&
    player.availability_status !== 'available' && player.availability_status !== 'confirmed_in'

  return (
    <div className={`lineup-player-row ${compact ? 'compact' : ''}`}>
      {/* Always render flag slot to maintain alignment */}
      <span className="player-flag">{flag || ''}</span>
      <span className="player-name">{player.name}</span>
      {/* Reserve space for badge to maintain alignment */}
      {!isConfirmed && (
        <span className="availability-badge-slot">
          {hasBadge ? (
            <AvailabilityBadge
              status={player.availability_status}
              reason={player.availability_reason}
            />
          ) : null}
        </span>
      )}
      {!compact && (
        <span className="player-age" title="Age">
          {age || '—'}
        </span>
      )}
      {showStats && !compact && (
        <div className="player-stats">
          <span className="player-stat goals" title="Goals">
            {seasonStats.goals || 0}
          </span>
          <span className="player-stat assists" title="Assists">
            {seasonStats.assists || 0}
          </span>
          <span className="player-stat" title="SOT">
            {seasonStats.shots_on_target || 0}
          </span>
          <span className="player-stat yellows" title="Yellow Cards">
            {seasonStats.yellows || 0}
          </span>
        </div>
      )}
      {/* Show start probability only for predicted lineups (not confirmed) */}
      {/* Always render to maintain alignment, show —% if no probability */}
      {!compact && !isConfirmed && (
        <span
          className="player-prob"
          style={{
            fontSize: '0.6875rem',
            color: player.start_probability >= 0.7 ? 'var(--green)' : player.start_probability >= 0.4 ? 'var(--yellow)' : 'var(--muted)',
            minWidth: 32,
            textAlign: 'right',
          }}
          title="Start probability"
        >
          {player.start_probability > 0 ? `${Math.round(player.start_probability * 100)}%` : '—%'}
        </span>
      )}
    </div>
  )
}

interface PositionGroupDisplayProps {
  label: string
  players: PlayerRow[]
  compact: boolean
  showStats: boolean
  isConfirmed?: boolean
}

function PositionGroupDisplay({ label, players, compact, showStats, isConfirmed }: PositionGroupDisplayProps) {
  if (players.length === 0) return null

  return (
    <div className="position-group">
      <div className="position-group-header">{label}</div>
      {players.map((player) => (
        <PlayerRowDisplay
          key={player.player_id}
          player={player}
          compact={compact}
          showStats={showStats}
          isConfirmed={isConfirmed}
        />
      ))}
    </div>
  )
}

function formatReturnDate(endDate: string | null | undefined): string | null {
  if (!endDate) return null
  try {
    const date = new Date(endDate)
    const now = new Date()
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return null // Already past
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays <= 7) return `${diffDays}d`
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)}w`

    // Format as date for longer periods
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  } catch {
    return null
  }
}

// Extended injury info with player stats
interface InjuredPlayerDisplay {
  player_id: number
  player_name: string
  position?: string | null
  injury_type: string
  status: 'out' | 'doubtful'
  end_date?: string | null
  // Stats from PlayerRow if available
  goals: number
  assists: number
  shots_on_target: number
  yellows: number
  age?: number
  nationality?: string
}

interface InjuredPlayersDisplayProps {
  injuries: InjuryInfo[]
  allPlayers: PlayerRow[] // Combined XI and bench to find player stats
  compact: boolean
  showStats: boolean
}

function InjuredPlayersDisplay({ injuries, allPlayers, compact, showStats }: InjuredPlayersDisplayProps) {
  if (injuries.length === 0) return null

  // Create a map of player_id to PlayerRow for quick lookup
  const playerMap = useMemo(() => {
    const map = new Map<number, PlayerRow>()
    allPlayers.forEach(p => map.set(p.player_id, p))
    return map
  }, [allPlayers])

  // Merge injury info with player stats
  // Age/nationality come directly from injury data (backend provides them)
  // Stats come from playerMap lookup if player is in squad
  const injuredPlayers: InjuredPlayerDisplay[] = useMemo(() => {
    return injuries.map(inj => {
      const playerData = playerMap.get(inj.player_id)
      const seasonStats = playerData?.windows?.season || {}

      return {
        player_id: inj.player_id,
        player_name: inj.player_name,
        position: inj.position,
        injury_type: inj.injury_type,
        status: inj.status,
        end_date: inj.end_date,
        goals: seasonStats.goals || 0,
        assists: seasonStats.assists || 0,
        shots_on_target: seasonStats.shots_on_target || 0,
        yellows: seasonStats.yellows || 0,
        // Use age/nationality from injury data (from backend), fall back to playerMap
        age: inj.age ?? (playerData as any)?.age,
        nationality: inj.nationality ?? (playerData as any)?.nationality,
      }
    })
  }, [injuries, playerMap])

  // Sort by goals + assists (most influential first)
  const sortedPlayers = useMemo(() => {
    return [...injuredPlayers].sort((a, b) => {
      const aContrib = (a.goals || 0) + (a.assists || 0)
      const bContrib = (b.goals || 0) + (b.assists || 0)
      return bContrib - aContrib
    })
  }, [injuredPlayers])

  return (
    <div
      style={{
        marginTop: '0.75rem',
        paddingTop: '0.5rem',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: '0.6875rem',
          color: '#ef4444',
          marginBottom: '0.5rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Injuries / Doubts
      </div>

      {sortedPlayers.map((player) => {
        const flag = countryToEmoji(player.nationality)
        const returnDate = formatReturnDate(player.end_date)
        const isDoubtful = player.status === 'doubtful'

        return (
          <div
            key={player.player_id}
            className={`lineup-player-row ${compact ? 'compact' : ''}`}
            style={{ opacity: 0.9 }}
          >
            {/* Always render flag slot to maintain alignment */}
            <span className="player-flag">{flag || ''}</span>
            <span className="player-name">
              {player.player_name}
              <span
                style={{
                  marginLeft: 4,
                  fontSize: '0.625rem',
                  color: isDoubtful ? '#f59e0b' : '#ef4444',
                  fontWeight: 400,
                }}
              >
                ({player.injury_type})
              </span>
            </span>
            {/* Empty badge slot for alignment with XI players */}
            <span className="availability-badge-slot" />
            {!compact && (
              <span className="player-age" title="Age">
                {player.age || '—'}
              </span>
            )}
            {showStats && !compact && (
              <div className="player-stats">
                <span className="player-stat goals" title="Goals">
                  {player.goals || 0}
                </span>
                <span className="player-stat assists" title="Assists">
                  {player.assists || 0}
                </span>
                <span className="player-stat" title="SOT">
                  {player.shots_on_target || 0}
                </span>
                <span className="player-stat yellows" title="Yellow Cards">
                  {player.yellows || 0}
                </span>
              </div>
            )}
            {!compact && (
              <span
                style={{
                  fontSize: '0.625rem',
                  color: 'var(--muted)',
                  minWidth: 32,
                  textAlign: 'right',
                }}
                title={returnDate ? `Expected return: ${player.end_date}` : undefined}
              >
                {returnDate ? `↩ ${returnDate}` : ''}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface TeamLineupProps {
  team: TeamLite
  players: PlayerRow[]
  bench: PlayerRow[]
  injuries: InjuryInfo[]
  isConfirmed: boolean
  compact: boolean
  showStats: boolean
}

function TeamLineup({ team, players, bench, injuries, isConfirmed, compact, showStats }: TeamLineupProps) {
  // Backend already filters unavailable players when building predicted XI
  // Trust the backend's selection - don't re-filter here as it causes player count mismatches
  // Players in the XI are the ones the backend determined should start
  const grouped = useMemo(() => groupPlayersByPosition(players), [players])

  // Combine all players for stats lookup
  const allPlayers = useMemo(() => [...players, ...bench], [players, bench])

  const positionLabels: Record<PositionGroup, string> = {
    GK: 'Goalkeepers',
    DEF: 'Defenders',
    MID: 'Midfielders',
    FWD: 'Forwards',
  }

  return (
    <div className="lineup-team">
      <div className="lineup-team-header">
        {team.crest_url && <img src={team.crest_url} alt="" />}
        <span className="team-name">{team.name}</span>
        {isConfirmed && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: '0.625rem',
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            CONFIRMED
          </span>
        )}
      </div>

      {/* Column headers for stats */}
      {showStats && !compact && (
        <div className="lineup-stats-header">
          <span className="header-name">Player</span>
          <span className="header-age">Age</span>
          <span className="header-stat">G</span>
          <span className="header-stat">A</span>
          <span className="header-stat">SOT</span>
          <span className="header-stat">Y</span>
          {!isConfirmed && <span className="header-prob">Start</span>}
        </div>
      )}

      {/* Starting XI */}
      <div>
        <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
          {isConfirmed ? 'Starting XI' : 'Predicted XI'}
        </div>
        {(['GK', 'DEF', 'MID', 'FWD'] as PositionGroup[]).map((pos) => (
          <PositionGroupDisplay
            key={pos}
            label={positionLabels[pos]}
            players={grouped[pos]}
            compact={compact}
            showStats={showStats}
            isConfirmed={isConfirmed}
          />
        ))}
      </div>

      {/* Bench - only show for confirmed lineups */}
      {isConfirmed && bench.length > 0 && (
        <div
          style={{
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              fontSize: '0.6875rem',
              color: 'var(--muted)',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            Substitutes
          </div>
          {bench.map((player) => (
            <PlayerRowDisplay
              key={player.player_id}
              player={player}
              compact={compact}
              showStats={showStats}
              isConfirmed={isConfirmed}
            />
          ))}
        </div>
      )}

      {/* Injuries/Doubts - hide for confirmed lineups */}
      {!isConfirmed && (
        <InjuredPlayersDisplay
          injuries={injuries}
          allPlayers={allPlayers}
          compact={compact}
          showStats={showStats}
        />
      )}
    </div>
  )
}

export function MALineupTab({ fixtureId, homeTeam, awayTeam }: MALineupTabProps) {
  const [mobileTeam, setMobileTeam] = useState<'home' | 'away'>('home')
  const compact = false
  const showStats = true
  const { data, error } = useFixturePlayers(fixtureId)

  if (error) {
    return <div className="ma-error">Failed to load lineup data</div>
  }

  if (!data) {
    return <div className="ma-loading">Loading lineup data...</div>
  }

  const {
    predicted_xi_home,
    predicted_xi_away,
    bench_home,
    bench_away,
    confirmed,
    lineup_confirmed_at,
    injuries_home,
    injuries_away,
  } = data

  return (
    <div className="lineup-section">
      {/* Confirmation banner */}
      {confirmed && lineup_confirmed_at && (
        <div
          style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            fontSize: '0.75rem',
            color: '#22c55e',
            textAlign: 'center',
          }}
        >
          Lineups confirmed at {new Date(lineup_confirmed_at).toLocaleTimeString()}
        </div>
      )}

      {/* Mobile team selector */}
      <div className="lineup-mobile-toggle">
            <button
              className={`lineup-team-toggle-btn ${mobileTeam === 'home' ? 'active' : ''}`}
              onClick={() => setMobileTeam('home')}
            >
              {homeTeam.crest_url && <img src={homeTeam.crest_url} alt="" />}
              <span>{homeTeam.name}</span>
            </button>
            <button
              className={`lineup-team-toggle-btn ${mobileTeam === 'away' ? 'active' : ''}`}
              onClick={() => setMobileTeam('away')}
            >
              {awayTeam.crest_url && <img src={awayTeam.crest_url} alt="" />}
              <span>{awayTeam.name}</span>
            </button>
          </div>

          {/* Two-column layout (desktop) / Single column (mobile) */}
          <div className="lineup-teams-grid">
            <div className={`lineup-team-wrapper ${mobileTeam === 'home' ? 'mobile-visible' : 'mobile-hidden'}`}>
              <TeamLineup
                team={homeTeam}
                players={predicted_xi_home}
                bench={bench_home}
                injuries={injuries_home}
                isConfirmed={confirmed}
                compact={compact}
                showStats={showStats}
              />
            </div>
            <div className={`lineup-team-wrapper ${mobileTeam === 'away' ? 'mobile-visible' : 'mobile-hidden'}`}>
              <TeamLineup
                team={awayTeam}
                players={predicted_xi_away}
                bench={bench_away}
                injuries={injuries_away}
                isConfirmed={confirmed}
                compact={compact}
                showStats={showStats}
              />
            </div>
          </div>
    </div>
  )
}
